import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TestimonialSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoSlideTimerRef = useRef(null);

  const testimonials = [
    {
      name: "Alex Chen",
      role: "CTO, InnovateTech",
      company: "InnovateTech Inc.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
      text: "The AI integration services from Oasis Logic have completely transformed our business operations. Their advanced algorithms improved our efficiency by 60%, and the team's expertise ensured a smooth transition. Worth every penny!",
      rating: 5,
      color: "rgba(56, 189, 248, 0.8)"
    },
    {
      name: "Sophia Reynolds",
      role: "Director of Digital",
      company: "Global Solutions Ltd.",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80",
      text: "I was impressed by the attention to detail and the personalized approach. Oasis Logic didn't just provide a service; they delivered a comprehensive solution tailored to our specific needs. Their AI tools have given us a significant competitive edge.",
      rating: 5,
      color: "rgba(168, 85, 247, 0.8)"
    },
    {
      name: "Marcus Johnson",
      role: "CEO",
      company: "FutureTech Innovations",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80",
      text: "Working with Oasis Logic has been a game-changer for our startup. Their machine learning algorithms have achieved prediction accuracy levels we never thought possible. The ROI we've seen in just six months has exceeded all expectations.",
      rating: 5,
      color: "rgba(74, 222, 128, 0.8)"
    },
    {
      name: "Elena Rodriguez",
      role: "Head of Operations",
      company: "TechAdvance Corp",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80",
      text: "The neural network solution developed by Oasis Logic has dramatically improved our data processing capabilities. Their team's technical expertise and responsive support made the implementation process remarkably smooth and effective.",
      rating: 5,
      color: "rgba(249, 115, 22, 0.8)"
    }
  ];

  const TRANSITION_INTERVAL = 6000; // 6 seconds between slides

  // Auto-transition functionality
  useEffect(() => {
    const startAutoSlide = () => {
      clearTimeout(autoSlideTimerRef.current);
      autoSlideTimerRef.current = setTimeout(() => {
        if (!isPaused) {
          setCurrentIndex(prevIndex => 
            prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
          );
        }
        startAutoSlide();
      }, TRANSITION_INTERVAL);
    };

    startAutoSlide();

    return () => clearTimeout(autoSlideTimerRef.current);
  }, [isPaused, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex(prevIndex => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex(prevIndex => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  // Generate rating stars
  const renderRatingStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <motion.span
        key={index}
        className={`star ${index < rating ? 'filled' : 'empty'}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 * index, duration: 0.3 }}
      >
        {index < rating ? "★" : "☆"}
      </motion.span>
    ));
  };

  // Generate animated network nodes in the background
  const generateNetworkNodes = () => {
    const nodes = [];
    const connections = [];
    const nodeCount = 20;
    
    for (let i = 0; i < nodeCount; i++) {
      const x = `${Math.random() * 100}%`;
      const y = `${Math.random() * 100}%`;
      const size = Math.random() * 8 + 4;
      const opacity = Math.random() * 0.5 + 0.1;
      const delay = Math.random() * 5;
      
      nodes.push(
        <motion.div
          key={`node-${i}`}
          className="network-node"
          style={{
            left: x,
            top: y,
            width: size,
            height: size,
            opacity
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [opacity, opacity * 1.5, opacity],
          }}
          transition={{
            duration: 4,
            delay,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      );
      
      // Add random connections between nodes
      if (i > 0 && Math.random() > 0.5) {
        const targetIndex = Math.floor(Math.random() * i);
        connections.push(
          <motion.div
            key={`connection-${i}-${targetIndex}`}
            className="network-connection"
            style={{
              left: x,
              top: y,
              width: '1px',
              height: '1px',
              opacity: opacity * 0.8
            }}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: opacity * 0.8 }}
            transition={{ duration: 2, delay: delay * 0.5 }}
          />
        );
      }
    }
    
    return [...nodes, ...connections];
  };

  return (
    <section 
      className="testimonial-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="section-header">
        <motion.h2
          className="gradient-text"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Client Success Stories
        </motion.h2>
        <motion.p
          className="section-subtext"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Real results from our valued partners
        </motion.p>
      </div>

      <div className="auto-slide-indicator">
        <div 
          className={`progress-bar ${isPaused ? 'paused' : ''}`}
          style={{ 
            animationDuration: `${TRANSITION_INTERVAL}ms`,
            animationPlayState: isPaused ? 'paused' : 'running'
          }}
        ></div>
      </div>
      
      <div className="testimonial-carousel-container">
        <div className="network-background">
          {generateNetworkNodes()}
        </div>
        
        <div className="testimonial-display">
          <AnimatePresence mode="wait">
            {testimonials.map((testimonial, index) => (
              index === currentIndex && (
                <motion.div
                  key={testimonial.name}
                  className="testimonial-card glass-morphism"
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -30 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    duration: 0.5 
                  }}
                >
                  <div className="testimonial-content">
                    <motion.div 
                      className="testimonial-text"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      <motion.div 
                        className="quote-icon"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 0.8, scale: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 21c3.33-3.33 5-6 5-8.99C8 9.89 7.11 9 6 9c-1.66 0-3 1.34-3 3 0 1.66 1.34 3 3 3-1 0-3-1-3-3C3 9.89 4.89 8 7 8c2.22 0 4 1.78 4 4 0 2.22-1.78 4-4 4m14 0c3.33-3.33 5-6 5-8.99C22 9.89 21.11 9 20 9c-1.66 0-3 1.34-3 3 0 1.66 1.34 3 3 3-1 0-3-1-3-3 0-2.11 1.89-4 4-4 2.22 0 4 1.78 4 4 0 2.22-1.78 4-4 4"></path>
                        </svg>
                      </motion.div>
                      
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                      >
                        {testimonial.text}
                      </motion.p>
                      
                      <motion.div 
                        className="rating-stars"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.5 }}
                      >
                        {renderRatingStars(testimonial.rating)}
                      </motion.div>
                    </motion.div>
                    
                    <motion.div 
                      className="testimonial-author"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8, duration: 0.5 }}
                    >
                      <div className="author-image-container">
                        <motion.div 
                          className="author-image-glow" 
                          style={{ boxShadow: `0 0 20px ${testimonial.color}` }}
                          animate={{
                            boxShadow: [
                              `0 0 20px ${testimonial.color}`, 
                              `0 0 40px ${testimonial.color}`, 
                              `0 0 20px ${testimonial.color}`
                            ]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "reverse"
                          }}
                        />
                        <motion.img 
                          src={testimonial.image} 
                          alt={testimonial.name} 
                          className="author-image"
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.9, duration: 0.5 }}
                        />
                        <motion.div 
                          className="ai-scan-effect"
                          animate={{ 
                            top: ['0%', '100%', '0%']
                          }}
                          transition={{ 
                            duration: 3, 
                            repeat: Infinity,
                            ease: "linear" 
                          }}
                        />
                      </div>
                      
                      <div className="author-info">
                        <motion.h4
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1.0, duration: 0.5 }}
                        >
                          {testimonial.name}
                        </motion.h4>
                        <motion.div 
                          className="author-role"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1.1, duration: 0.5 }}
                        >
                          <span className="role">{testimonial.role}</span>
                          <span className="company">{testimonial.company}</span>
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
                  
                  <motion.div 
                    className="testimonial-badge"
                    style={{ backgroundColor: testimonial.color }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                      delay: 0.2 
                    }}
                  >
                    <span className="badge-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
                      </svg>
                    </span>
                    Verified Client
                  </motion.div>
                  
                  <div className="tech-corners">
                    <div className="tech-corner top-left"></div>
                    <div className="tech-corner top-right"></div>
                    <div className="tech-corner bottom-left"></div>
                    <div className="tech-corner bottom-right"></div>
                  </div>

                  <motion.div 
                    className="ai-circuit-pattern"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.15 }}
                    transition={{ delay: 0.3, duration: 1 }}
                  />
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </div>

        <div className="carousel-controls testimonial-controls">
          <motion.button 
            className="carousel-button"
            onClick={() => {
              prevTestimonial();
              setIsPaused(true);
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.span 
              animate={{ x: [-2, 2, -2] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              ←
            </motion.span>
          </motion.button>
          
          <motion.div 
            className="carousel-indicators"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                className={`carousel-dot ${currentIndex === index ? 'active' : ''}`}
                onClick={() => {
                  goToTestimonial(index);
                  setIsPaused(true);
                }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                animate={{ 
                  scale: currentIndex === index ? [1, 1.2, 1] : 1,
                  backgroundColor: currentIndex === index ? 'var(--primary-color)' : 'var(--text-secondary)'
                }}
                transition={{ 
                  duration: currentIndex === index ? 1.5 : 0.3, 
                  repeat: currentIndex === index ? Infinity : 0,
                  repeatType: "reverse"
                }}
              />
            ))}
          </motion.div>
          
          <motion.button 
            className="carousel-button"
            onClick={() => {
              nextTestimonial();
              setIsPaused(true);
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.span 
              animate={{ x: [2, -2, 2] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              →
            </motion.span>
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
